import type { ReactNode } from "react";
import { Badge, DataTable, DescriptionList, type ColumnDef, SectionCard } from "@/deps/ui-library";
import { DocumentHistory } from "@/components/DocumentHistory";
import { PageHeader } from "@/components/PageHeader";
import { DocumentStatusBadge } from "@/components/list/StatusBadge";
import type { DocumentHistory as History, DocumentStatus } from "@/api/types";

/**
 * The frame every document detail shares: a header carrying the number and status, the document
 * itself, a side column of facts, and the history.
 *
 * The `locked` chip is a separate thing from the status. "Approved" says where it got to in the
 * ladder; "Locked" says it can no longer be changed. A screen that shows only the first leaves
 * somebody looking for an Edit button that will never appear.
 */
export function DocumentScreen({
  title,
  number,
  status,
  badge,
  locked,
  subtitle,
  actions,
  facts,
  history,
  children,
  aside,
}: {
  title: string;
  number: string | null;
  status?: DocumentStatus;
  /** In place of the status, for a document that isn't on the approval ladder. */
  badge?: ReactNode;
  locked?: boolean;
  subtitle?: string;
  actions?: ReactNode;
  facts: Array<{ label: string; value: ReactNode }>;
  history?: History;
  children: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={number ?? `${title} · Draft`}
        subtitle={subtitle}
        actions={
          <span className="flex items-center gap-2">
            {badge ?? (status ? <DocumentStatusBadge status={status} /> : null)}
            {locked ? (
              <Badge
                colorScheme="default"
                size="sm"
                title="Issued and filed. Correct it with a credit note."
              >
                Locked
              </Badge>
            ) : null}
            {actions}
          </span>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="flex flex-col gap-4 lg:col-span-2">{children}</div>

        <div className="flex flex-col gap-4">
          <SectionCard title="Details">
            <DescriptionList.Root className="flex flex-col gap-2">
              {facts.map((fact) => (
                <DescriptionList.Row
                  key={fact.label}
                  className="flex items-baseline justify-between gap-3 p-0"
                >
                  <DescriptionList.Term size="sm" className="text-muted-foreground font-normal">
                    {fact.label}
                  </DescriptionList.Term>
                  <DescriptionList.Details className="text-right text-sm">
                    {fact.value}
                  </DescriptionList.Details>
                </DescriptionList.Row>
              ))}
            </DescriptionList.Root>
          </SectionCard>

          {aside}

          {history ? <DocumentHistory history={history} /> : null}
        </div>
      </div>
    </div>
  );
}

interface Line {
  n: number;
  label: string;
  account: string;
  quantity: string;
  unitPrice: string;
  tax: string;
  net: string;
}

const RIGHT = { textAlign: "right" as const, cellClassName: "num" };

const LINE_COLUMNS: Array<ColumnDef<Line>> = [
  { id: "label", header: "Description", cell: ({ row }) => row.original.label },
  {
    id: "account",
    header: "Account",
    cell: ({ row }) => row.original.account,
    meta: { cellClassName: "text-muted-foreground text-xs" },
  },
  { id: "quantity", header: "Qty", cell: ({ row }) => row.original.quantity, meta: RIGHT },
  { id: "unitPrice", header: "Unit price", cell: ({ row }) => row.original.unitPrice, meta: RIGHT },
  {
    id: "tax",
    header: "Tax",
    cell: ({ row }) => row.original.tax,
    meta: { cellClassName: "text-muted-foreground text-xs" },
  },
  { id: "net", header: "Amount", cell: ({ row }) => row.original.net, meta: RIGHT },
];

/** A read-only table of document lines, shared by invoice, credit note and bill details. */
export function LinesTable({ lines, caption }: { lines: Line[]; caption: string }) {
  return (
    <DataTable
      aria-label={caption}
      size="sm"
      enableSorting={false}
      columns={LINE_COLUMNS}
      data={lines}
      getRowId={(line) => String(line.n)}
      // Every line, always: a document is not paged.
      pagination={{ mode: "manual", show: false }}
    />
  );
}
